import {
  Button,
  Icon,
  Loader,
  Popover,
  Search,
} from "@screencloud/screencloud-ui-components";
import classNames from "clsx";
import { UUID } from "@screencloud/uuid";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  AllPlaylistsInOrgDocument,
  AllPlaylistsInOrgQuery,
  AllPlaylistsInSpaceDocument,
  AllPlaylistsInSpaceQuery,
  Playlist,
} from "src/types.g";
import { isOrgWidePath } from "src/utils/orgWideFeature";
import { updateAndToggleSelectedItems } from "../../helpers/updateAndToggleSelectedItemsHelper";
import { useAppContext } from "../../hooks/useAppContext";
import EmptyState from "../EmptyState";
import { ApolloProps, withData } from "./apollo";
import SearchPlaylistComponent from "./search";
import { Styled } from "./styles";
import { Item } from "./Item";
import { MediaPickerSelectedTagList } from "../MediaPickerSelectedTagList";
import { TagFilterDropdown } from "../TagFilterDropdown";
import { useGetScreenSize } from "src/hooks/useGetScreenSize";
import { useGetTagsByPlaylists } from "./hooks/useGetTagsByPlaylists";
export interface PlaylistPickerProps {
  multiple?: boolean;
  allowSelectAll?: boolean;
  callback?: (playlistIds: string[], playlist: Playlist[]) => void;
  excludeShare?: boolean;
  spaceId?: UUID;
}

export const PlaylistPickerComponent = (
  props: PlaylistPickerProps & ApolloProps,
): JSX.Element => {
  const [selectedArray, setSelectedArray] = useState<string[]>([]);
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const context = useAppContext();

  const {
    allowSelectAll,
    callback,
    multiple,
    searchString,
    query: searchQuery,
    updateSearchString,
    isSearch,
    clearSearch,
    playlistData,
    onClickTag,
    selectedTags,
    onSetelectTags,
    onClearTags,
    hasSelectedTags,
  } = props;
  const { isTabletView } = useGetScreenSize();
  const tags = useGetTagsByPlaylists();
  const currentSpaceId = context?.currentSpace?.id;
  const { playlists, renderFetchMoreButton, isLoading } = playlistData;
  const isPlaylistSelected = (id: UUID): boolean => selectedArray.includes(id);
  const playlistPickerCallback = (id: string, playlist: Playlist) => {
    const result = updateAndToggleSelectedItems(
      id,
      playlist,
      selectedArray,
      selectedData,
      multiple,
    );
    if (callback) {
      callback(result.selectedArray, result.selectedData);
    }
    setSelectedArray(result.selectedArray);
    setSelectedData(result.selectedData);
  };

  const onSearchSelectAll = (ids: string[], selectedPlaylists: Playlist[]) => {
    setSelectedArray(ids);
    setSelectedData(selectedPlaylists);
  };

  const onSelectAllChange = async () => {
    if (selectedArray.length <= 0) {
      let playlists = [] as Playlist[];
      if (!isSearch) {
        if (isOrgWidePath()) {
          const { data } = await props.client!.query<AllPlaylistsInOrgQuery>({
            fetchPolicy: "network-only",
            query: AllPlaylistsInOrgDocument,
            variables: {
              orgId: context.currentSpace?.id,
            },
          });
          playlists = data.orgById?.playlistsByOrgId?.nodes as Playlist[];
        } else {
          const { data } = await props.client!.query<AllPlaylistsInSpaceQuery>({
            fetchPolicy: "network-only",
            query: AllPlaylistsInSpaceDocument,
            variables: {
              spaceId: context.currentSpace?.id,
            },
          });
          playlists = data.spaceById?.publishedPlaylistsBySpaceId
            ?.nodes as Playlist[];
        }
      }
      const selectedArray = playlists
        ? playlists.map((playlist) => playlist?.id)
        : [];
      const selectedData = playlists as any;

      setSelectedArray(selectedArray);
      setSelectedData(selectedData);
      setIsSelectAll(true);
      if (callback) {
        callback(selectedArray, selectedData);
      }
    } else {
      setSelectedArray([]);
      setSelectedData([]);
      setIsSelectAll(false);
      if (callback) {
        callback([], []);
      }
    }
  };

  const renderHeader = () => (
    <div
      className={classNames("content-header", {
        "media-tags": hasSelectedTags,
      })}
    >
      <div className="search-filter-container">
        <Search
          className="search"
          onClear={clearSearch}
          onChange={(e, { value }) => updateSearchString(value)}
          placeholder="Search Playlists"
          showNoResults={false}
          value={searchString}
        />
        <>
          <TagFilterDropdown
            className="filter-dropdown"
            selectedTags={selectedTags}
            spaceId={context.user.settings.spaceId}
            onSelect={onSetelectTags}
            overlayWhenClick={isTabletView}
            availableTags={tags as string[]}
          />
          <Button
            onClick={onClearTags}
            className="clear-all-tags-button"
            data-testid="clear-all-tags-button"
            disabled={!hasSelectedTags}
          >
            <Icon name="close" />
            <FormattedMessage
              id="ui_component.common.label.clear_all"
              defaultMessage="Clear All"
            />
          </Button>
        </>
      </div>

      {hasSelectedTags && (
        <MediaPickerSelectedTagList
          selectedTags={selectedTags}
          onClearTags={onClearTags}
          onClickTag={onClickTag}
        />
      )}

      {allowSelectAll && (
        <div className="search-checkbox">
          <Popover
            trigger={
              <Button icon onClick={onSelectAllChange}>
                {playlists.length === selectedArray.length &&
                selectedArray.length > 0 ? (
                  <Icon name="checkbox-checked" />
                ) : selectedArray.length > 0 ? (
                  <Icon name="checkbox-deselect" />
                ) : (
                  <Icon name="checkbox-empty" />
                )}
              </Button>
            }
            content={selectedArray.length > 0 ? "Deselect All" : "Select All"}
            className="checkbox"
            position="top center"
            inverted
          />
        </div>
      )}
    </div>
  );

  return (
    <Styled className="media-content playlist">
      {renderHeader()}
      <div
        className={classNames("container", {
          "has-selected-tags": hasSelectedTags,
        })}
      >
        <div className="media-item-header subheader">
          <div className="media-core">
            <h3>Playlists</h3>
          </div>
        </div>
        <div
          className="layout-list"
          data-testid="layout-list"
          id="scrollableDiv"
        >
          {isSearch ? (
            <SearchPlaylistComponent
              allowSelectAll={allowSelectAll}
              searchString={searchQuery}
              multiple={multiple}
              callback={callback}
              toggleSelect={playlistPickerCallback}
              selectedArray={selectedArray}
              selectedData={selectedData}
              isSelectAll={isSelectAll}
              spaceId={props.spaceId}
              onSearchSelectAll={onSearchSelectAll}
            />
          ) : isLoading ? (
            <Loader active />
          ) : playlists.length === 0 ? (
            <EmptyState section="playlist" className="empty">
              <h3>
                <FormattedMessage
                  id="playlists.empty.empty_text"
                  defaultMessage="No playlist added yet"
                />
              </h3>
              <p>
                <FormattedMessage
                  id="playlists.empty.help_text"
                  defaultMessage="Create a playlist to add to your channel"
                />
              </p>
            </EmptyState>
          ) : (
            <>
              {playlists.map((playlist, index) => {
                const currentPlaylist = playlist?.draft || playlist.published;
                const isPlaylistOwner = currentSpaceId === playlist?.spaceId;
                return (
                  currentPlaylist !== null && (
                    <Item
                      isSelected={isPlaylistSelected(playlist?.id)}
                      playlist={playlist}
                      allowSelectAll={allowSelectAll}
                      isOwner={isPlaylistOwner}
                      index={index}
                      callback={playlistPickerCallback}
                      onClickTag={onClickTag}
                    />
                  )
                );
              })}
              {renderFetchMoreButton}
            </>
          )}
        </div>
      </div>
    </Styled>
  );
};

export default withData(
  PlaylistPickerComponent,
) as React.ComponentType<PlaylistPickerProps>;
